import { Component, inject } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { IDistrict, IGrades, ILookup, IRoles } from '@core/interfaces/ilookup';
import { IProcessedColumn, IProcessedRow, IUserUploadDto } from '@core/interfaces/iupload-process';
import { AuthService } from '@core/services/auth.service';
import { LookupService } from '@core/services/lookup.service';
import { ToastService } from '@core/services/toast.service';
import { environment } from '@env/environment';
import { take, tap, finalize } from 'rxjs';
import * as XLSX from 'xlsx';
import { UserManagementService } from '../../../services/user-management.service';
import { Role } from '@core/enums/roles';
import { HelperService } from '@core/services/helper.service';
import { ISchool } from '@views/features/management/interfaces/isearchSchool';
import { IUserFilteredLookup } from '@core/interfaces/iuser';
import { isBlockedEmailDomain } from '@core/utils';

@Component({
  selector: 'prism-user-upload',
  templateUrl: './user-upload.component.html',
  styleUrl: './user-upload.component.scss',
})
export class UserUploadComponent {
  private toastService = inject(ToastService);
  private authService = inject(AuthService);
  private lookupService = inject(LookupService);
  private userManagementService = inject(UserManagementService);
  private helperService = inject(HelperService);
  private resourceContainer = 'resources';
  private sanitizer = inject(DomSanitizer);

  user = this.authService.getCurrentUser();
  isDistrictAdmin = this.user.roleId == Role.DistrictAdmin;
  file: any;
  arrayBuffer: any;
  fileList: any;
  roles: IRoles[] = [];
  organizations: IDistrict[] = [];
  schools: ISchool[] = [];
  headers: any;
  uploadData: any[] = [];
  tableData: any[] = [];
  finalData: IUserUploadDto[] = [];
  hasError = false;
  canUpload = false;
  fileLink = environment.uploadFileLink;
  sasToken: string = '';
  sanitizedSasToken!: SafeUrl;
  currentDistricts: IDistrict[] = [];
  currentSchools: ISchool[] = [];
  grades: IGrades[] = [];
  isDownloading = false;

  ngOnInit(): void {
    this.getSasToken();
    this.getUserFilteredLookup();
  }

  getSasToken(): void {
    this.authService
      .getStorageSasToken(this.resourceContainer)
      .pipe(
        take(1),
        tap((res: any) => (this.sasToken = res.value))
      )
      .subscribe();
  }

  generateUrlWithSasToken(blobUrl?: string) {
    if (this.sasToken) {
      const urlWithSasToken = `${blobUrl}${this.sasToken}`;
      return this.sanitizer.bypassSecurityTrustResourceUrl(urlWithSasToken);
    }
    return '';
  }

  getUserFilteredLookup(): void {
    this.lookupService.getUserFilteredLookup().pipe(
      take(1),
      tap((res: IUserFilteredLookup) => {
        this.currentDistricts = res.districts;
        this.currentSchools = res.schools;
        this.grades = res.grades;
      })
    ).subscribe();
  }

  addFile(event: any) {
    this.hasError = false;
    this.tableData = [];
    this.headers = [];
    this.canUpload = false;

    const fileInput = event.target; // Capture the input element
    this.file = fileInput.files[0];

    if (!this.file) return; // Check if a file is selected

    let fileReader = new FileReader();
    fileReader.readAsArrayBuffer(this.file);
    fileReader.onload = (e) => {
      this.arrayBuffer = fileReader.result;
      var data = new Uint8Array(this.arrayBuffer);
      var arr = new Array();
      for (var i = 0; i != data.length; ++i)
        arr[i] = String.fromCharCode(data[i]);
      var bstr = arr.join('');
      var workbook = XLSX.read(bstr, { type: 'binary' });
      var first_sheet_name = workbook.SheetNames[0];
      var worksheet = workbook.Sheets[first_sheet_name];
      this.headers = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0];
      this.uploadData = XLSX.utils.sheet_to_json(worksheet, { raw: true });

      // Check if the uploadData exceeds 500 rows
      if (this.uploadData.length > 500) {
        this.handleValidationFailure('You can only upload up to 500 users at a time.', fileInput);
        return;
      }
      
      // Check headers for required columns
      if (this.headers && this.headers.length > 0) {
        const requiredColumns = ['firstName', 'lastName', 'emailName', 'districtId', 'schoolId', 'gradeId'];
        
        const columnHeaders = this.headers.map((name: any) => String(name).toLowerCase());
        const missingColumns = requiredColumns.filter(col => !columnHeaders.includes(col.toLowerCase()));
        
        if (missingColumns.length > 0) {
          this.handleValidationFailure(`Missing headers: ${missingColumns.join(', ')}.`, fileInput);
          return;
        }
        
        // Check if headers are in correct order
        const actualOrder = this.headers.map((name: any) => String(name));
        
        for (let i = 0; i < requiredColumns.length; i++) {
          if (actualOrder[i].toLowerCase() !== requiredColumns[i].toLowerCase()) {
            this.handleValidationFailure(`Headers must be in this exact order: ${requiredColumns.join(', ')}.`, fileInput);
            return;
          }
        }
        
        // Check for extra columns
        const extraColumns = this.headers.filter((col: string) => !requiredColumns.some(displayCol => displayCol.toLowerCase() === col.toLowerCase()));
        if (extraColumns.length > 0) {
          this.handleValidationFailure(`Extra columns found: ${extraColumns.join(', ')}.`, fileInput);
          return;
        }
        
        // Check if the file contains any data rows
        if (this.uploadData.length === 0) {
          this.handleValidationFailure('File does not contain any records.', fileInput);
          return;
        }
      }
      
      // Process each row in uploadData
      this.uploadData.forEach((row) => {
        let processed: any[] = [];
        this.headers.forEach((prop: string) => {
          let colData = {
            name: prop,
            displayValue: row[prop],
            value: row[prop],
            invalid: false,
          };
          processed.push(colData);
        });
        this.tableData.push(processed);
      });
      // Reset file input to allow re-uploading the same file
      fileInput.value = '';
    };
  }

  validate() {
    let hasError = false;
    let districtColValue: number | null = null;
    let shownDistrictError = false;
    let shownSchoolError = false;
    let shownBlockedEmailError = false;

    for (var i = 0; i < this.tableData.length; i++) {
      var row = this.tableData[i];
      for (var j = 0; j < row.length; j++) {
        var column = row[j];
        column.invalid = false;

        // Ignore casing
        const normalizedColumnName = column.name.toLowerCase();
        
        if (normalizedColumnName === 'districtid') {
          districtColValue = column.value;
        }

        switch (normalizedColumnName) {
          case 'districtid': {
             if (column.value == '' || column.value == null) {
              column.invalid = true;
            } else if (this.isDistrictAdmin) {
              if (!this.checkIfIdIsValid(column.value, this.currentDistricts)) {
                column.invalid = true;
                if (!shownDistrictError) {
                  this.toastService.error('You cannot upload users for another district.');
                  shownDistrictError = true;
                }
              }
            } else {
              const org = this.currentDistricts.find((d) => d.id === Number(column.value));
              if (!org) column.invalid = true;
            }
            break;
          }

          case 'schoolid': {
            if (column.value == '' || column.value == null) {
              column.invalid = true;
            } else if (this.isDistrictAdmin) {
              if (!this.checkIfIdIsValid(column.value, this.currentSchools)) {
                column.invalid = true;
                if (!shownSchoolError) {
                  this.toastService.error('You cannot upload users for another school.');
                  shownSchoolError = true;
                }
              }
            } else {
              const school = this.currentSchools.find((s) => s.id === Number(column.value) && s.districtId === Number(districtColValue));
              if (!school) column.invalid = true;
            }
            break;
          }

          case 'gradeid': {
            const gradeIds = column.value
              ?.toString()
              .split(',')
              .map((id: string) => id.trim())
              .filter((id: string) => id !== '');

            const matchedGrades = gradeIds
              .map((id: string) => this.grades.find((grade) => grade.id === Number(id)))
              .filter((g: string) => g !== undefined);

            const areValid = matchedGrades.length === gradeIds.length;

            if (!areValid || matchedGrades.length === 0) {
              column.invalid = true;
            } else {
              column.matchedGrades = matchedGrades;
            }
            break;
          }

          case 'emailname': {
            const email = (column.displayValue ?? column.value ?? '').toString().trim();

            if (!email || !this.helperService.emailRegex.test(email) || email.length > 50) {
              column.invalid = true;
              break;
            }

            const duplicateCount = this.tableData.filter((r) => {
              const v = (r[j].displayValue ?? r[j].value ?? '').toString().trim().toLowerCase();
              return v === email.toLowerCase();
            }).length;

            if (duplicateCount > 1) {
              column.invalid = true;
              column.error = 'Duplicate email in file.';
              break;
            }

            const { isBlocked, domainName } = isBlockedEmailDomain(email);
            if (isBlocked) {
              column.invalid = true;
              column.error = `Emails from ${domainName} are not allowed.`;
              if (!shownBlockedEmailError) {
                this.toastService.error(`Invalid email extension(s) in file. Please correct and re-upload. Only work emails allowed.`);
                shownBlockedEmailError = true;
              }
            }
            break;
          }

          case 'firstname':

          case 'lastname': {
             if (column.value == '' || column.value == null ||
              column.value.length > 50) {
              column.invalid = true;
            }
            else if (/[0-9]/.test(column.value)) {
              column.invalid = true;
            }
            break;
          }

          default:
            break;
        }
      }

      row.invalid = row.filter((r: any) => r.invalid).length > 0;
      if (row.invalid) {
        hasError = true;
      }
    }

    this.canUpload = !hasError;
    if (this.canUpload) {
      this.processFinalData(); //this shapes the data needed by the api
      this.toastService.success('File validated. Ready to be submitted');
    } else {
      this.toastService.error(
        'Please fix errors on the file before submitting.'
      );
    }
  }

  processFinalData(): void {
    this.finalData = [];

    this.tableData.forEach((row: IProcessedRow) => {
      const user: Partial<IUserUploadDto> = {};

      row.forEach((column: IProcessedColumn) => {
        const normalizedColumnName = column.name.toLowerCase();
        switch (normalizedColumnName) {
          case 'gradeid':
            if (column.matchedGrades && column.matchedGrades.length) {
              const gradeIds = column.matchedGrades
                .map((g: IGrades) => g.id)
                .filter((id: number, index: number, self: number[]) => self.indexOf(id) === index);
              user.gradeId = gradeIds.join(',');
            } else {
              user.gradeId = '';
            }
            break;

          case 'districtid':
            user.districtId = String(column.value);
            break;

          case 'schoolid':
            user.schoolId = Number(column.value);
            break;

          case 'firstname':
            user.firstName = column.value as string;
            break;
          case 'lastname':
            user.lastName = column.value as string;
            break;
          case 'emailname':
            user.emailName = column.value as string;
            break;

          default:
            break;
        }
      });

      this.finalData.push(user as IUserUploadDto);
    });
  }

  checkIfIdIsValid(id: number, list: ILookup[]): boolean {
    return list.some((item: ILookup) => item.id === Number(id));
  }

  edit(row: { edit: boolean }) {
    this.canUpload = false;
    this.tableData.forEach((t) => (t.edit = false));
    row.edit = true;
  }

  upload() {
    this.finalData.forEach((user) => {
      user.roleId = Role.User; // Adding District users only.
    })
    this.userManagementService.addUsers(this.finalData).pipe(
      take(1),
      tap((res: boolean) => {
        if (res) {
          this.toastService.success('Upload successful');
          this.tableData = [];
          this.headers = [];
          this.canUpload = false;
        }
      })
    ).subscribe();
  }

  private handleValidationFailure(message: string, fileInput: HTMLInputElement): void {
    this.toastService.error(message);
    this.headers = [];
    this.uploadData = [];
    fileInput.value = '';
  }

  onDownloadTemplate(event: Event): void {
  event.preventDefault();

  if (this.isDownloading) return;

  this.isDownloading = true;
  this.toastService.info('Downloading template, please wait');

  this.userManagementService.downloadBulkTemplate()
    .pipe(
      take(1),
      finalize(() => this.isDownloading = false)
    )
    .subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Prism-BulkUserUploadTemplate.xlsx';
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.toastService.error('Unable to download template.')
    });
}
}
